import moment from "moment"

export const DEFAULT_JOB = {
  name: '',
  description: '',
  enabled: true,
  endpointUrl: '',
  method: 'GET',
  headersText: '{}',
  body: '',
  scheduleType: 'recurring',
  recurrence: 'daily',
  timeOfDay: '09:00',
  dayOfWeek: 1,
  dayOfMonth: 1,
  month: 1,
  intervalValue: 5,
  intervalUnit: 'minutes'
}

export const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

export const methodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
  .map(method => ({ value: method, text: method }))

export const scheduleTypeOptions = [
  { value: 'recurring', text: 'Set time' },
  { value: 'interval', text: 'Interval' }
]

export const recurrenceOptions = [
  { value: 'daily', text: 'Daily' },
  { value: 'weekly', text: 'Weekly' },
  { value: 'monthly', text: 'Monthly' },
  { value: 'yearly', text: 'Yearly' }
]

export const intervalUnitOptions = [
  { value: 'seconds', text: 'Seconds' },
  { value: 'minutes', text: 'Minutes' },
  { value: 'hours', text: 'Hours' }
]

export const awsIntervalUnitOptions = [
  { value: 'minutes', text: 'Minutes' },
  { value: 'hours', text: 'Hours' },
  { value: 'days', text: 'Days' }
]

export const mongoIntervalUnitOptions = [
  { value: 'minutes', text: 'Minutes' },
  { value: 'hours', text: 'Hours' }
]

export const dayOfWeekOptions = dayNames.map((dayName, index) => ({
  value: index,
  text: dayName
}))

export const monthOptions = monthNames.map((monthName, index) => ({
  value: index + 1,
  text: monthName
}))

export const getJobId = (job) => job?.id || job?._id

const copyableJobFields = [
  'description',
  'enabled',
  'endpointUrl',
  'method',
  'headers',
  'body',
  'scheduleType',
  'recurrence',
  'timeOfDay',
  'dayOfWeek',
  'dayOfMonth',
  'month',
  'intervalValue',
  'intervalUnit'
]

export const getCopyJobName = (name = '', jobs = []) => {
  const baseName = `${name || 'Job'} (copy)`
  const existingNames = new Set((jobs || []).map(job => job?.name).filter(Boolean))

  if (!existingNames.has(baseName)) {
    return baseName
  }

  let copyNumber = 2
  let copyName = `${name || 'Job'} (copy ${copyNumber})`

  while (existingNames.has(copyName)) {
    copyNumber += 1
    copyName = `${name || 'Job'} (copy ${copyNumber})`
  }

  return copyName
}

export const toCopiedJob = (job = {}, jobs = []) => {
  const copiedJob = {
    isClone: true,
    name: getCopyJobName(job.name, jobs)
  }

  copyableJobFields.forEach(field => {
    if (job[field] !== undefined) {
      copiedJob[field] = job[field]
    }
  })

  return copiedJob
}

export const formatDateTime = (date) => {
  const mDate = moment(date)
  return mDate.isValid() ? mDate.format('M/D/YYYY h:mm A') : ''
}

export const formatDuration = (durationMs) => {
  if (!durationMs && durationMs !== 0) {
    return ''
  }

  return durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(1)}s`
}

export const normalizeTimeOfDay = (timeOfDay = '') => {
  return `${timeOfDay}`.substring(0, 5)
}

export const formatSchedule = (job) => {
  const timeOfDay = normalizeTimeOfDay(job.timeOfDay)

  if (job.scheduleType === 'custom') {
    return job.scheduleExpression || 'Custom schedule'
  }

  if (job.scheduleType === 'interval') {
    return `Every ${job.intervalValue} ${job.intervalUnit}`
  }

  if (job.recurrence === 'weekly') {
    return `${dayNames[job.dayOfWeek] || 'Weekly'} at ${timeOfDay}`
  }

  if (job.recurrence === 'monthly') {
    return `Monthly on day ${job.dayOfMonth} at ${timeOfDay}`
  }

  if (job.recurrence === 'yearly') {
    return `${monthNames[(job.month || 1) - 1]} ${job.dayOfMonth} at ${timeOfDay}`
  }

  return `Daily at ${timeOfDay}`
}

export const toFormJob = (job = {}) => {
  return {
    ...DEFAULT_JOB,
    ...job,
    headersText: JSON.stringify(job.headers || {}, null, 2),
    body: typeof job.body === 'string'
      ? job.body
      : (job.body ? JSON.stringify(job.body, null, 2) : '')
  }
}
